/**
 * CSRF Token Utilities
 *
 * Provides helpers to check expiration and transparently refresh
 * the CSRF token stored in the ACL Zustand store.
 */
import { aclService } from '@/app/services/acl';
import { useAclStore } from '@/app/stores/aclStore';

/** 1-minute buffer before the real expiration (ms). */
const EXPIRY_BUFFER_MS = 60 * 1000;

/**
 * Returns `true` when the CSRF token is absent or within the expiry buffer window.
 *
 * @param expiration - ISO datetime string from `csrf_token_expiration`, or `null`.
 */
export const isCsrfTokenExpired = (expiration: string | null): boolean => {
  if (!expiration) return true;
  const expirationMs = new Date(expiration).getTime();
  return expirationMs <= Date.now() + EXPIRY_BUFFER_MS;
};

/** Unix timestamp (seconds) 24 hours from now — used as the default CSRF token lifetime. */
const defaultExpiration = () => Math.floor(Date.now() / 1000) + 24 * 60 * 60;

/**
 * Returns the current CSRF token if still valid, otherwise fetches a fresh one
 * from the API, persists it in the ACL store, and returns the new token.
 *
 * Safe to call outside React components (uses `getState()` for non-reactive access).
 *
 * @param expirationDateTime - Desired expiration for a newly requested token as a
 *   Unix timestamp (seconds). Defaults to 24 hours from now.
 * @returns The valid CSRF token string, or `null` if the fetch fails.
 */
export const refreshCsrfTokenIfExpired = async (
  expirationDateTime?: number
): Promise<string | null> => {
  const { csrfToken, csrfTokenExpiration, setCsrfToken } =
    useAclStore.getState();

  if (csrfToken && !isCsrfTokenExpired(csrfTokenExpiration)) {
    return csrfToken;
  }

  try {
    const tokenData = await aclService.getCsrfToken(
      expirationDateTime ?? defaultExpiration(),
    );
    setCsrfToken(tokenData.csrf_token, tokenData.csrf_token_expiration);
    return tokenData.csrf_token;
  } catch (error) {
    console.error('[csrfToken] Failed to refresh CSRF token:', error);
    return null;
  }
};
